@use "Styles/mixins";
@use "Styles/includes/variables";

:host {
    // Prevents outside styles from being applied, since link-overlay will apply them
    display: contents;
}

.video-card {
    // Inherit all styles from hos, so that link-overlay "becomes" the host element
    // and can be styled from the outside
    all: inherit;

    // `vm-link-overlay` is the layout container now: the primary link covers it
    // as an absolutely-positioned overlay, while the thumbnail and info column
    // flow as flex/grid siblings.
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 16px;
    isolation: isolate;

    @at-root :host([variant="search"]) & {
        display: grid;
        grid-template-columns: 1fr auto;
        // Two rows by default; a third "tags" row is inserted via :has()
        // only when the tags element is present. This prevents the empty
        // tags track from contributing phantom row-gaps.
        grid-template-areas:
            "heading heading"
            "details thumb";
        column-gap: 16px;
        row-gap: 8px;
        align-items: start;

        &:has(.video-card__tags) {
            grid-template-areas:
                "heading heading"
                "tags    tags"
                "details thumb";
        }

        @include mixins.desktop-and-up {
            display: flex;
            grid-template-columns: none;
            grid-template-areas: none;
            align-items: flex-start;
        }
    }

    &__link {
        // Primary link, slotted into `vm-link-overlay`'s `link-overlay` slot.
        // The cover-the-card positioning (and elevating the byline's secondary
        // links above it) is owned by `vm-link-overlay`, which adopts its sheet
        // into this shadow root — so only the link's own visual reset lives here.
        text-decoration: none;
        color: inherit;
    }

    &__img-container {
        position: relative;
        flex: 0 0 120px;
        height: auto;
        aspect-ratio: 16 / 9;
        border-radius: 10px;
        overflow: hidden;

        @include mixins.container-desktop-and-up {
            flex: 0 0 160px;
        }

        &::before {
            content: " ";
            position: absolute;
            inset: 0;
            background-color: mixins.get-color(background, bg-overlay);
            opacity: 0.2;
            pointer-events: none;
        }

        @at-root :host([variant="search"]) & {
            grid-area: thumb;
            // Override the default-variant `flex: 0 0 120px` since the parent
            // is a grid here, not a flex row.
            flex: none;
            width: 120px;
            // Vertically centre the thumbnail against the byline+meta column
            // (matches Figma's `items-center` on that row).
            align-self: center;

            // The default variant bumps to 160×90 on wide containers — undo
            // that here; search uses the same mobile thumbnail until desktop.
            @include mixins.container-desktop-and-up {
                flex: none;
                width: 120px;
            }

            @include mixins.desktop-and-up {
                flex: 0 0 224px;
                align-self: auto;
            }
        }
    }

    &__img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;

        &--placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            background: mixins.get-color(background, bg-overlay);
            color: mixins.get-color(text, inverse);
            font-size: mixins.get-font-size(text-xs);
            font-weight: 600;
            letter-spacing: 0.04em;
        }
    }

    &__duration {
        position: absolute;
        inset: auto 8px 8px auto;
        box-sizing: border-box;
        height: 20px;
        padding: 2px 8px;
        border-radius: 10px;
        background: mixins.get-color(background, bg-black-alpha70);
        font-size: mixins.get-font-size(text-xs);
        font-weight: 500;
        line-height: 16px;
        color: mixins.get-color(text, inverse);
    }

    &__info {
        display: flex;
        flex-direction: column;
        min-width: 0;

        @at-root :host([variant="search"]) & {
            // `display: contents` removes `.video-card__info` from layout so
            // its children (title / tags / details) participate directly in
            // the parent grid via their `grid-area` assignments below.
            display: contents;

            @include mixins.desktop-and-up {
                display: flex;
                flex-direction: column;
                min-width: 0;
            }
        }
    }

    &__title {
        margin: 0;
        font-size: mixins.get-font-size(text-sm);
        line-height: mixins.get-line-height(text-sm);
        font-weight: 900;
        color: mixins.get-color(text, text-primary);
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        line-clamp: 3;
        display: -webkit-box;
        overflow: hidden;
        @include mixins.desktop-and-up {
            -webkit-line-clamp: 4;
            line-clamp: 4;
        }

        @at-root :host([variant="search"]) & {
            grid-area: heading;
            font-size: mixins.get-font-size(text-sm);
            line-height: mixins.get-line-height(text-sm);

            @include mixins.desktop-and-up {
                font-size: mixins.get-font-size(text-lg);
                line-height: mixins.get-line-height(text-lg);
                // Heading → details gap is owned by `.video-card__details`'s
                // top margin on desktop, not by the heading.
                margin-bottom: 0;
            }
        }
    }

    &__byline {
        margin: 0;
        font-size: mixins.get-font-size(text-xs);
        line-height: mixins.get-line-height(text-xs);
        color: mixins.get-color(text, text-tertiary);
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;

        // Empty named slots have no light DOM children and contribute nothing
        // to layout, so missing publisher / badges / contributors collapse
        // without leaving gaps.
        &:empty {
            display: none;
        }

        a {
            color: mixins.get-color(text, text-tertiary);
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }

        @at-root :host([variant="search"]) & {
            margin: 0;
            font-size: mixins.get-font-size(text-sm);
            line-height: mixins.get-line-height(text-sm);
            // 2-line clamp on mobile so longer contributor lists wrap legibly.
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            white-space: normal;
            overflow: hidden;

            @include mixins.desktop-and-up {
                // Single-line ellipsis in the wider desktop column.
                display: block;
                white-space: nowrap;
                text-overflow: ellipsis;
                -webkit-line-clamp: initial;
                line-clamp: initial;
            }
        }
    }

    &__meta {
        display: flex;
        align-items: center;
        gap: 4px;
        margin: 0;
        font-size: mixins.get-font-size(text-xs);
        line-height: mixins.get-line-height(text-xs);
        color: mixins.get-color(text, text-placeholder);

        p {
            margin: 0;
        }

        &:empty {
            display: none;
        }

        @at-root :host([variant="search"]) & {
            // 4px byline→meta gap is owned by `.video-card__details`'s flex gap.
            margin: 0;
        }
    }

    &__tags {
        list-style: none;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin: 0;

        @include mixins.desktop-and-up {
            margin: 8px 0px;
        }

        @at-root :host([variant="search"]) & {
            grid-area: tags;
        }
    }

    &__tag {
        display: inline-flex;
        align-items: center;
        padding: 3px 12px;
        border-radius: 20px;
        background: mixins.get-color(background, bg-brand-secondary);
        color: mixins.get-color(text, text-brand-secondary-hover);
        font-size: mixins.get-font-size(text-xs);
        font-weight: 700;
        line-height: mixins.get-line-height(text-xs);
    }

    &__details {
        @at-root :host([variant="search"]) & {
            grid-area: details;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
    }
}
